// src/Reports.js
import React, { useState } from "react";
import { apiFetch } from "./utils/apiUtils";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
} from "@mui/material";
import AppointmentsByPetSize from "./AppointmentsByPetSize";

// Define the list of available reports.
const reportsList = [
  { id: "groomerScheduleChange", title: "Groomer Schedule Change" },
  { id: "groomerRebooks", title: "Groomer Rebooks" },
  { id: "appointmentsByPetSize", title: "Appointments by Pet Size" },
  // Additional reports can be added here.
];

// Component for the Groomer Rebooks Report
const GroomerRebooksReport = () => {
  const [monthYear, setMonthYear] = useState("");
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [reportGeneratedAt, setReportGeneratedAt] = useState(null);

  // Get the current month and year for default value
  const getCurrentMonthYear = () => {
    const now = new Date();
    // Format as YYYY-MM
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  };

  // Month names for the report title
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  // Format month-year for display
  const formatMonthYear = (monthYearStr) => {
    if (!monthYearStr) return "";
    
    const [year, month] = monthYearStr.split('-');
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  const handleRunReport = () => {
    if (!monthYear) {
      setError("Please select a month and year.");
      return;
    }
    
    setLoading(true);
    setError("");
    
    // Call the API endpoint
    apiFetch(`/api/reports/groomer-rebooks?month_year=${monthYear}`)
      .then((res) => {
        if (!res.ok) {
          return res.text().then(text => { 
            throw new Error("Failed to fetch report data: " + text);
          });
        }
        return res.json();
      })
      .then((data) => {
        setReportData(data);
        setReportGeneratedAt(new Date());
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error running report:", err);
        setError("Error running report: " + err.message);
        setLoading(false);
      });
  };

  // Group data by groomer
  const getGroomerSummary = () => {
    if (!reportData || reportData.length === 0) return [];
    
    const groomerMap = {};
    
    reportData.forEach(record => {
      if (!groomerMap[record.createdBy]) {
        groomerMap[record.createdBy] = {
          groomerName: record.createdBy,
          appointments: 0,
          pets: 0,
          appointmentDetails: []
        };
      }
      
      groomerMap[record.createdBy].appointments++;
      groomerMap[record.createdBy].pets += record.numPets;
      groomerMap[record.createdBy].appointmentDetails.push(record);
    });
    
    return Object.values(groomerMap).sort((a, b) => 
      a.groomerName.localeCompare(b.groomerName)
    );
  };

  const groomerSummary = getGroomerSummary();

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Groomer Rebooks Report
      </Typography>
      
      {/* Report criteria form */}
      <Box
        component="form"
        sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 400, mb: 2 }}
      >
        <TextField
          label="Month and Year"
          type="month"
          fullWidth
          InputLabelProps={{ shrink: true }}
          value={monthYear}
          onChange={(e) => setMonthYear(e.target.value)}
          placeholder={getCurrentMonthYear()}
        />
        
        <Button 
          variant="contained" 
          onClick={handleRunReport}
          disabled={loading}
        >
          {loading ? "Loading..." : "Run Report"}
        </Button>
      </Box>
      
      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {/* Report header with title and generation timestamp */}
      {reportGeneratedAt && (
        <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6">Groomer Rebooks Report - {formatMonthYear(monthYear)}</Typography>
          <Typography variant="caption">
            Report generated: {reportGeneratedAt.toLocaleString()}
          </Typography>
        </Box>
      )}
      
      {/* Report results display */}
      {reportData && reportData.length > 0 ? (
        <Box sx={{ mt: 2 }}>
          {/* Summary table by groomer */}
          <Typography variant="h6" gutterBottom>Summary by Groomer</Typography>
          <TableContainer component={Paper} sx={{ mb: 4 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Groomer</TableCell>
                  <TableCell align="right">Appointments</TableCell>
                  <TableCell align="right">Pets</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groomerSummary.map((groomer, idx) => (
                  <TableRow key={idx}>
                    <TableCell component="th" scope="row">{groomer.groomerName}</TableCell>
                    <TableCell align="right">{groomer.appointments}</TableCell>
                    <TableCell align="right">{groomer.pets}</TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell component="th" scope="row"><strong>Total</strong></TableCell>
                  <TableCell align="right"><strong>{reportData.length}</strong></TableCell>
                  <TableCell align="right"><strong>{reportData.reduce((sum, record) => sum + record.numPets, 0)}</strong></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Detailed appointments table */}
          <Typography variant="h6" gutterBottom>Detailed Appointments</Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Groomer</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Pet</TableCell>
                  <TableCell>Creation Date</TableCell>
                  <TableCell>Appointment Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.map((record, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{record.createdBy}</TableCell>
                    <TableCell>{record.customerName}</TableCell>
                    <TableCell>{record.petName}</TableCell>
                    <TableCell>{record.creationDate}</TableCell>
                    <TableCell>{record.apptDate}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Button variant="contained" sx={{ mt: 2 }} onClick={() => window.print()}>
            Print / Save as PDF
          </Button>
        </Box>
      ) : reportGeneratedAt && (
        <Typography variant="body1" sx={{ mt: 4, textAlign: 'center' }}>
          No rebook data found for the selected month.
        </Typography>
      )}
    </Box>
  );
};

// Component for the Groomer Schedule Change Report
const GroomerScheduleChangeReport = () => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [reportData, setReportData] = useState(null);
  const [reportGeneratedAt, setReportGeneratedAt] = useState(null);

  const handleRunReport = () => {
    if (!startDate || !endDate) {
      alert("Please select both a start and an end date.");
      return;
    }
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      status: statusFilter,
    }).toString();
    // Using apiFetch which automatically handles authorization headers and URL resolution
    apiFetch(`/api/reports/event-history?${params}`)
      .then((res) => {
        console.log("Response status:", res.status);
        if (!res.ok) {
          return res.text().then(text => { 
            console.error("Response text:", text);
            throw new Error("Failed to fetch report data: " + text);
          });
        }
        return res.json();
      })
      .then((data) => {
        setReportData(data);
        setReportGeneratedAt(new Date());
      })
      .catch((err) => {
        console.error("Error running report:", err);
        alert("Error running report: " + err.message);
      });
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Groomer Schedule Change Report
      </Typography>
      {/* Report criteria form */}
      <Box
        component="form"
        sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 400, mb: 2 }}
      >
        <TextField
          label="Start Date"
          type="date"
          fullWidth
          InputLabelProps={{ shrink: true }}
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
        />
        <TextField
          label="End Date"
          type="date"
          fullWidth
          InputLabelProps={{ shrink: true }}
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
        />
        <FormControl fullWidth>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="All">All</MenuItem>
            <MenuItem value="Scheduled">Scheduled</MenuItem>
            <MenuItem value="Called Out">Called Out</MenuItem>
            <MenuItem value="Filled In">Filled In</MenuItem>
            <MenuItem value="Time Off">Time Off</MenuItem>
            <MenuItem value="Event">Event</MenuItem>
          </Select>
        </FormControl>
        <Button variant="contained" onClick={handleRunReport}>
          Run Report
        </Button>
      </Box>
      {/* Report header with title and generation timestamp */}
      {reportGeneratedAt && (
        <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6">Groomer Schedule Change Report</Typography>
          <Typography variant="caption">
            Report generated: {reportGeneratedAt.toLocaleString()}
          </Typography>
        </Box>
      )}
      {/* CHANGED: Report results display formatted similar to the cell history window */}
      {reportData && reportData.length > 0 && (
        <Box sx={{ mt: 2 }}>
          {reportData.map((record, idx) => (
            <Box key={idx} sx={{ mb: 1 }}>
              <Typography variant="caption" display="block" sx={{ fontWeight: "bold" }}>
                HISTORY CREATION DATE: {new Date(record.timestamp).toLocaleDateString()} TIME: {new Date(record.timestamp).toLocaleTimeString()}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                Action: {record.action} | Name: {record.name} | Status: {record.status} | Note: {record.note} | User: {record.user}
              </Typography>
            </Box>
          ))}
          <Button variant="contained" sx={{ mt: 2 }} onClick={() => window.print()}>
            Print / Save as PDF
          </Button>
        </Box>
      )}
    </Box>
  );
};

const Reports = () => {
  const [selectedReport, setSelectedReport] = useState(null);

  const handleReportSelect = (report) => {
    setSelectedReport(report);
  };

  return (
    <Box sx={{ display: "flex", height: "100%", p: 2 }}>
      {/* Left panel: List of reports */}
      <Box sx={{ width: "30%", borderRight: "1px solid #ccc", pr: 2 }}>
        <Typography variant="h6" gutterBottom>
          Reports
        </Typography>
        <List>
          {reportsList.map((report) => (
            <ListItem key={report.id} disablePadding>
              <ListItemButton
                selected={selectedReport?.id === report.id}
                onClick={() => handleReportSelect(report)}
              >
                <ListItemText primary={report.title} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
      {/* Right panel: Report criteria and results */}
      <Box sx={{ flexGrow: 1, pl: 2 }}>
        {selectedReport ? (
          <>
          {selectedReport.id === "groomerScheduleChange" ? (
            <GroomerScheduleChangeReport />
          ) : selectedReport.id === "groomerRebooks" ? (
            <GroomerRebooksReport />
          ) : selectedReport.id === "appointmentsByPetSize" ? (
            <AppointmentsByPetSize />
          ) : (
            <Typography variant="body1">Report not defined.</Typography>
          )}
          </>
        ) : (
          <Typography variant="body1">Select a report from the list.</Typography>
        )}
      </Box>
    </Box>
  );
};

export default Reports;
